import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { Message as MessageT, User } from '@/lib/api';
import { Conversations } from '@/lib/api';
import Message from './Message';
import TypingIndicator from './TypingIndicator';
import ScrollToBottom from './ScrollToBottom';
import { useChatStore } from '@/stores/chat';
import { formatDateDivider } from '@/lib/format';

dayjs.extend(utc);

interface Props {
  conversationId: string;
  messages: MessageT[];
  currentUser: User | null;
  onReply: (m: MessageT) => void;
  onEdit: (m: MessageT) => void;
  onDelete: (m: MessageT) => void;
  onReplyClick: (m: MessageT) => void;
}

type Row =
  | { kind: 'divider'; key: string; label: string }
  | { kind: 'msg'; key: string; msg: MessageT; isGrouped: boolean; showSender: boolean };

const STICK_TO_BOTTOM_PX = 80;

export default function MessageList({ conversationId, messages, currentUser, onReply, onEdit, onDelete, onReplyClick }: Props) {
  const parentRef = useRef<HTMLDivElement>(null);
  const typing = useChatStore((s) => s.typingByConversation.get(conversationId));
  const [loadError, setLoadError] = useState<string | null>(null);
  // F-003: derived from the same `wasAtBottomRef` we already track for the
  // auto-scroll heuristic. Re-renders trigger a separate state update so
  // the floating button can mount / unmount without forcing the whole
  // message list to re-measure.
  const [scrolledUp, setScrolledUp] = useState(false);
  // Number of messages that arrived while the user was parked away from
  // the bottom — drives the badge on the scroll-to-bottom button.
  const [unread, setUnread] = useState(0);
  const seenLengthRef = useRef(0);

  // Resolve typing users against the current member list so the typing
  // indicator shows a real name instead of "Someone". The WS broadcast
  // only carries `userId` (the canonical, unambiguous key) so we have to
  // look up display info from the store here.
  const members = useChatStore((s) => (conversationId ? s.membersByConversation.get(conversationId) : undefined));
  const typingUsers = useMemo<User[]>(() => {
    if (!typing) return [];
    const memberById = new Map<string, User | null>();
    if (members) {
      for (const m of members) {
        memberById.set(m.userId, m.user ?? null);
      }
    }
    return Array.from(typing.userIds)
      .filter((id) => id !== currentUser?.id)
      .map((id) => {
        const u = memberById.get(id);
        if (u) return u;
        // Fallback if we don't yet have members loaded (e.g. very first
        // message ever in the conversation): at least show the raw id so
        // the UI doesn't lie with a placeholder name.
        return { id, username: '', displayName: '', avatarUrl: null, lastSeen: null } as User;
      });
  }, [typing, currentUser, members]);
  const typingName = typingUsers[0]?.displayName || typingUsers[0]?.username || 'Someone';

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    let prev: MessageT | null = null;
    let lastDateKey: string | null = null;
    for (const m of messages) {
      // Defensive: createdAt might be missing on an incoming ws broadcast
      // before the REST GET/messages round-trip. Fall back to a sentinel so
      // the list still renders rather than crashing with TypeError.
      const ts = m.createdAt || new Date(0).toISOString();
      // Parse the UTC ISO string with `dayjs.utc(...).local()` so the
      // date key matches the SAME local calendar day as `formatDateDivider`.
      // Without this, a UTC 23:00 message and a UTC 01:00 message can land
      // in the same YYYY-MM-DD slice but different local days, and the
      // divider label ("Today"/"Yesterday") would disagree with the date key
      // — different browsers in different timezones would render different
      // orderings.
      const localTs = dayjs.utc(ts).local();
      const dateKey = localTs.format('YYYY-MM-DD');
      if (dateKey !== lastDateKey) {
        out.push({ kind: 'divider', key: `d-${dateKey}-${m.id}`, label: formatDateDivider(ts) });
        lastDateKey = dateKey;
      }
      const sameSender = prev !== null && prev.senderId === m.senderId && prev.senderId !== '';
      const prevTs = prev?.createdAt || ts;
      const withinGroupWindow =
        prev !== null && localTs.valueOf() - dayjs.utc(prevTs).local().valueOf() < 60_000;
      const isGrouped = sameSender && withinGroupWindow;
      out.push({
        kind: 'msg',
        key: m.id,
        msg: m,
        isGrouped,
        showSender: !isGrouped,
      });
      prev = m;
    }
    return out;
  }, [messages]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (i) => (rows[i]?.kind === 'divider' ? 36 : 64),
    overscan: 12,
    getItemKey: (i) => rows[i]?.key ?? i,
  });

  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [initialScrollDone, setInitialScrollDone] = useState(false);
  const wasAtBottomRef = useRef(true);

  const scrollToBottom = useCallback(() => {
    if (rows.length === 0) return;
    virtualizer.scrollToIndex(rows.length - 1, { align: 'end' });
    wasAtBottomRef.current = true;
    setScrolledUp(false);
    setUnread(0);
    seenLengthRef.current = messages.length;
  }, [rows.length, virtualizer, messages.length]);

  // Track bottom position so we know whether to autoscroll.
  const handleScroll = useCallback(() => {
    const el = parentRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    const wasAtBottom = dist <= STICK_TO_BOTTOM_PX;
    wasAtBottomRef.current = wasAtBottom;
    setScrolledUp(!wasAtBottom);
    if (wasAtBottom) {
      // User parked back at the bottom — clear the unread badge and align
      // the "last seen" watermark so we don't double-count when more
      // messages land later.
      setUnread(0);
      seenLengthRef.current = messages.length;
    }
  }, [messages.length]);

  // F-003: when new messages arrive while we're scrolled up, bump the
  // unread counter. The auto-scroll effect below uses `wasAtBottomRef` to
  // decide whether to actually scroll, so this only matters for the badge.
  useEffect(() => {
    if (!initialScrollDone) return;
    if (wasAtBottomRef.current) {
      seenLengthRef.current = messages.length;
      setUnread(0);
      return;
    }
    const incoming = messages.length - seenLengthRef.current;
    if (incoming > 0) {
      // Only count messages from OTHER users toward the badge. The user
      // typing in the composer shouldn't see "1 new" for their own
      // optimistic send.
      const otherUsers = messages
        .slice(seenLengthRef.current)
        .filter((m) => m.senderId !== currentUser?.id).length;
      setUnread((n) => n + Math.max(0, otherUsers));
      seenLengthRef.current = messages.length;
    }
  }, [messages, initialScrollDone, currentUser?.id]);

  // Initial scroll to bottom.
  useEffect(() => {
    if (!initialScrollDone && messages.length > 0 && parentRef.current) {
      virtualizer.scrollToIndex(rows.length - 1, { align: 'end' });
      setInitialScrollDone(true);
      seenLengthRef.current = messages.length;
    }
  }, [initialScrollDone, messages.length, rows.length, virtualizer]);

  // When new messages arrive and user was at bottom, autoscroll.
  useEffect(() => {
    if (!initialScrollDone) return;
    if (!wasAtBottomRef.current) return;
    if (rows.length === 0) return;
    virtualizer.scrollToIndex(rows.length - 1, { align: 'end' });
  }, [rows.length, virtualizer, initialScrollDone]);

  // Load older on reaching top
  const onScroll = useCallback(async (e: React.UIEvent<HTMLDivElement>) => {
    handleScroll();
    const el = e.currentTarget;
    if (loadingOlder || !hasMore || messages.length === 0) return;
    if (el.scrollTop < 80) {
      const oldest = messages[0];
      if (!oldest) return;
      setLoadingOlder(true);
      const prevHeight = el.scrollHeight;
      try {
        const { messages: older } = await Conversations.messages(conversationId, { before: oldest.id, limit: 50 });
        if (older.length === 0) {
          setHasMore(false);
        } else {
          // append older to head of store
          const store = useChatStore.getState();
          const existing = store.messagesByConversation.get(conversationId) ?? [];
          store.setMessages(conversationId, [...older, ...existing], true);
          // adjust scroll so the user stays at the same message
          requestAnimationFrame(() => {
            const newHeight = el.scrollHeight;
            el.scrollTop = newHeight - prevHeight;
          });
        }
      } catch (err) {
        // The previous comment claimed this was surfaced via ChatPage's
        // toast, but nothing actually called into ChatPage. Surface the
        // failure through the same retry banner as handleRetry so the
        // user sees the error and can retry, instead of silently
        // thinking they've hit the end of history.
        const msg = err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to load older messages';
        setLoadError(msg);
      } finally {
        setLoadingOlder(false);
      }
    }
  }, [conversationId, hasMore, loadingOlder, messages, handleScroll]);

  // Reset load-error state when conversation changes.
  useEffect(() => {
    setLoadError(null);
    setHasMore(true);
    setInitialScrollDone(false);
    setScrolledUp(false);
    setUnread(0);
    seenLengthRef.current = 0;
  }, [conversationId]);

  // Expose loadError via a retry handler the parent can wire up.
  const handleRetry = useCallback(async () => {
    setLoadError(null);
    try {
      const { messages: list } = await Conversations.messages(conversationId, { limit: 50 });
      useChatStore.getState().setMessages(conversationId, list, true);
    } catch (e) {
      const msg = e && typeof e === 'object' && 'message' in e
        ? String((e as { message: unknown }).message)
        : 'Failed to load messages';
      setLoadError(msg);
    }
  }, [conversationId]);

  return (
    <div
      ref={parentRef}
      onScroll={onScroll}
      className="nice-scroll relative flex-1 overflow-y-auto bg-ink-50 dark:bg-ink-900"
      role="log"
      aria-live="polite"
      aria-relevant="additions"
    >
      {loadError && (
        <div
          role="alert"
          data-testid="message-list-error"
          className="mx-3 mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          <span aria-hidden="true">⚠️</span>
          <span className="flex-1">{loadError}</span>
          <button type="button" className="btn-ghost px-2 py-0.5 text-xs" onClick={handleRetry}>
            Retry
          </button>
        </div>
      )}
      {messages.length === 0 ? (
        <div
          className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-sm text-ink-500 dark:text-ink-300"
          data-testid="empty-state"
        >
          <span aria-hidden="true" className="text-4xl">💬</span>
          <span className="font-medium text-ink-700 dark:text-ink-100">
            No messages yet
          </span>
          <span className="max-w-xs text-xs text-ink-500 dark:text-ink-300">
            Send your first message to start the conversation.
          </span>
        </div>
      ) : (
        <div style={{ height: `${virtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}>
          {virtualizer.getVirtualItems().map((vi) => {
            const row = rows[vi.index];
            if (!row) return null;
            return (
              <div
                key={row.key}
                data-index={vi.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${vi.start}px)`,
                }}
              >
                {row.kind === 'divider' ? (
                  <div className="my-2 flex justify-center">
                    <span className="rounded-full bg-ink-200 px-3 py-0.5 text-xs text-ink-600 dark:bg-ink-700 dark:text-ink-200">
                      {row.label}
                    </span>
                  </div>
                ) : (
                  <Message
                    message={row.msg}
                    isOwn={row.msg.senderId === currentUser?.id}
                    isGrouped={row.isGrouped}
                    showSender={row.showSender}
                    onReply={onReply}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onReplyClick={onReplyClick}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
      {typingUsers.length > 0 && (
        <div className="sticky bottom-0 left-0 px-4 pb-1">
          <TypingIndicator name={typingName} />
        </div>
      )}
      {/* F-003: floating scroll-to-bottom button. Mounted absolutely inside
          the scroll container so it tracks the message viewport on iOS. */}
      <ScrollToBottom visible={scrolledUp} onClick={scrollToBottom} unreadCount={unread} />
    </div>
  );
}
